import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Result,
  MathDiv,
  ActionButtonsRow,
  Select,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  AlertDiv,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { MdError } from 'react-icons/md'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function MansoryBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const mansoryGradient = 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)'
  const [fpkf, setFpkf] = useState('')
  const [fyk, setFyk] = useState('')
  const [b, setB] = useState('')
  const [d, setD] = useState('')
  const [mk, setMk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [rhoMin, setRhoMin] = useState(0.15)
  const [selectedRebar, setSelectedRebar] = useState('Ø5.0')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'mansory-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFpkf(entry.fpkf)
      setFyk(entry.fyk)
      setB(entry.b)
      setD(entry.d)
      setMk(entry.mk)
      setGammaf(entry.gammaf || 1.4)
      setRhoMin(entry.rhoMin)
      setResult(entry.result)
      setData(entry.data)
      setSelectedRebar(entry.selectedRebar)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fpkf = validateField('fpkf', fpkf)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.d = validateField('d', d)
    newErrors.mk = validateField('mk', mk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.rhoMin = validateField('rhoMin', rhoMin)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fpkf, fyk, b, d, mk, gammaf, rhoMin, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        b,
        d,
        md: mk * gammaf,
        fpkf,
        fyk,
        rhoMin,
      }

      setData(data)

      const response = await api.post('/mansory/bending', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fpkf,
        fyk,
        b,
        d,
        mk,
        gammaf,
        rhoMin,
        result: response.data,
        data,
        selectedRebar,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      b,
      d,
      mk,
      gammaf,
      fpkf,
      fyk,
      rhoMin,
      selectedRebar,
      result,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setB(jsonData.b)
        setD(jsonData.d)
        setMk(jsonData.mk)
        setGammaf(jsonData.gammaf || 1.4)
        setFpkf(jsonData.fpkf)
        setFyk(jsonData.fyk)
        setRhoMin(jsonData.rhoMin)
        setSelectedRebar(jsonData.selectedRebar)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
      errorMessage: 'Erro ao copiar os cálculos. Tente novamente.',
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient={mansoryGradient}>
        <div className="header-content">
          <BackButton to="/alvenaria">
            <FiArrowLeft /> Voltar para Alvenaria
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/alvenaria">Alvenaria</a>
            <span>/</span>
            <span>Flexão</span>
          </div>
          <h1>🧱 Flexão em Alvenaria Estrutural</h1>
          <p className="subtitle">
            Dimensionamento e verificação de elementos de alvenaria estrutural submetidos à flexão simples
          </p>
          <div className="badges">
            <Badge>📋 NBR 16868-1:2020</Badge>
            <Badge>🧮 Cálculo de Armadura</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard>
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de elementos de alvenaria estrutural submetidos à flexão
              simples, conforme a NBR 16868-1:2020.
            </p>
            <p>
              O cálculo verifica se o momento resistente máximo é adequado e determina a área de aço necessária,
              considerando as propriedades dos materiais e a geometria da seção.
            </p>
            <div className="norms">
              <span>NBR 16868-1:2020</span>
              <span>Tabelas de Armadura</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Materiais</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Resistência do Prisma Cheio (fpk*, MPa)</span>
              <ValidatedInput
                name="fpkf"
                type="number"
                value={fpkf || ''}
                onChange={(event) => setFpkf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura útil (cm)</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento Característico (kN.cm)</span>
              <ValidatedInput
                name="mk"
                type="number"
                value={mk || ''}
                onChange={(event) => setMk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Outros</h3>
            <InputDiv>
              <span>Taxa de aço mínima (%)</span>
              <ValidatedInput
                name="rhoMin"
                type="number"
                value={rhoMin || ''}
                onChange={(event) => setRhoMin(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`M_{Rd,máx} = ${result.mrdmax.toFixed(2)} \\, kN \\cdot cm`} />
                {result.mrdmax >= data.md ? (
                  <BlockMath math={`M_{Rd,máx} \\geq M_d`} />
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`M_{Rd,máx} < M_d`} />
                      <MdError />
                    </AlertDiv>
                    <p>O momento solicitante é maior que o momento limite.</p>
                  </>
                )}
                {result.mrdmax >= data.md && (
                  <>
                    <BlockMath math={`A_{s} = ${result.as && result.as.toFixed(2)} \\, cm^2`} />
                    <InputDiv>
                      <spa>Seleção da bitola</spa>
                      <Select
                        id="case2RebarSelect"
                        value={selectedRebar}
                        onChange={(event) => setSelectedRebar(event.target.value)}
                      >
                        {result.asRebars.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Serão necessários {result.asRebars.find((r) => r.name == selectedRebar).quantity} barras de{' '}
                      {selectedRebar}
                    </p>
                  </>
                )}
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Momento máximo</h3>
              <MathDiv>
                <BlockMath math={`f_d=0,7 \\cdot \\dfrac{{f_{pk}}^*}{2} `} />
                <BlockMath
                  math={`f_d=0,7 \\cdot \\dfrac{${data.fpkf / 10}}{2} = ${result.fd.toFixed(3)} \\, kN/cm^2 `}
                />
                <BlockMath math={`M_{Rd,máx} \\leq 0,3 \\cdot f_d \\cdot b \\cdot d^2  `} />
                <BlockMath
                  math={`M_{Rd,máx} \\leq 0,3 \\cdot ${result.fd.toFixed(3)} \\cdot ${data.b} \\cdot ${data.d}^2  `}
                />
                <BlockMath math={`\\boxed{M_{Rd,máx} = ${result.mrdmax.toFixed(2)} \\, kN \\cdot cm }`} />
                {result.mrdmax >= data.md ? (
                  <BlockMath math={`M_{Rd,máx} \\geq M_d`} />
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`M_{Rd,máx} < M_d`} />
                      <MdError />
                    </AlertDiv>
                    <p>O momento solicitante é maior que o momento limite.</p>
                  </>
                )}
              </MathDiv>
              {result.mrdmax >= data.md && (
                <>
                  <h3>Armadura</h3>
                  <MathDiv>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`x = 1,25 \\cdot d \\cdot \\left[ 1 - \\left(  1 - \\dfrac{M_d}{0,5 \\cdot b \\cdot d^2 \\cdot f_d} \\right)^{\\frac{1}{2}}  \\right]  `}
                      />
                      <BlockMath
                        math={`x = 1,25 \\cdot ${data.d} \\cdot \\left[ 1 - \\left(  1 - \\dfrac{${data.md.toFixed(2)}}{0,5 \\cdot ${data.b} \\cdot ${data.d}^2 \\cdot ${result.fd.toFixed(3)}} \\right)^{\\frac{1}{2}}  \\right]  `}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`x = 1,25 \\cdot d \\cdot \\left[ 1 - A  \\right]  `} />
                      <BlockMath math={`x = 1,25 \\cdot ${data.d} \\cdot \\left[ 1 - A  \\right]  `} />
                      <BlockMath
                        math={`A=  \\left(  1 - \\dfrac{M_d}{0,5 \\cdot b \\cdot d^2 \\cdot f_d} \\right)^{\\frac{1}{2}}`}
                      />
                      <BlockMath
                        math={`A=  \\left(  1 - \\dfrac{${data.md.toFixed(2)}}{0,5 \\cdot ${data.b} \\cdot ${data.d}^2 \\cdot ${result.fd.toFixed(3)}} \\right)^{\\frac{1}{2}}`}
                      />
                    </MobileOnlyDiv>
                    <BlockMath math={`\\boxed{x = ${result.x && result.x.toFixed(2)} \\, cm }`} />
                    <BlockMath math={`z = d - 0,4 \\cdot x  `} />
                    <BlockMath
                      math={`z = ${data.d} - 0,4 \\cdot ${result.x && result.x.toFixed(2)} = ${result.z && result.z.toFixed(2)} \\, cm  `}
                    />
                    <BlockMath
                      math={`A_{s,calc} = \\dfrac{M_d}{z \\cdot f_{yd}} =  \\dfrac{${data.md.toFixed(2)}}{${result.z && result.z.toFixed(2)} \\cdot ${result.fyd.toFixed(2)} }`}
                    />
                    <BlockMath math={`A_{s,calc} = ${result.ascalc && result.ascalc.toFixed(2)} \\, cm^2`} />
                    <BlockMath math={`A_{s,min} = ${data.rhoMin} \\% \\cdot b \\cdot h`} />
                    <BlockMath math={`A_{s,min} = ${data.rhoMin} \\% \\cdot ${data.b} \\cdot ${data.d}`} />
                    <BlockMath math={`A_{s,min} = ${result.asmin.toFixed(2)} \\, cm^2`} />
                    <BlockMath math={`\\boxed{A_{s} = ${result.as && result.as.toFixed(2)} \\, cm^2}`} />
                    <InputDiv>
                      <spa>Seleção da bitola</spa>
                      <Select
                        id="case2RebarSelect"
                        value={selectedRebar}
                        onChange={(event) => setSelectedRebar(event.target.value)}
                      >
                        {result.asRebars.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Serão necessários {result.asRebars.find((r) => r.name == selectedRebar).quantity} barras de{' '}
                      {selectedRebar}
                    </p>
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
